import React, { Dispatch, SetStateAction, useState } from 'react'
import { FlatList, StyleSheet, Pressable, ListRenderItem } from 'react-native'
import { DocumentORM, Variant } from 'src/types/types'
import { DisplayModes } from 'src/components/DNA/hooks/useDisplayMode'
import { DNAText, DNABox, DNAButton, DNADivider, GridList, Iffy, DNAChip } from '@alucio/lux-ui'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import {
  DNADocumentListRow,
  DocumentSearchResultRow,
  getFieldConfigsForTenant,
} from 'src/components/DNA'
import DNADocumentDefaultCard from 'src/components/DNA/Grid/Cards/default/DNADocumentDefaultCard'
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions'
import { useUserTenant } from 'src/state/redux/selector/user'
import { useAppSettings } from 'src/state/context/AppSettings'
import { SelectedDocuments } from './DocumentSelectManager'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'

const S = StyleSheet.create({
  MobileRow: {
    paddingHorizontal: 16,
  },
  DataCell: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  cardSpacerStyle: { marginBottom: 16 },
  accordionContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors['color-ui2-brand-600'],
    shadowColor: colors['color-black'],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.16,
    shadowRadius: 11.22,
    elevation: 4,
    backgroundColor: colors['color-white'],
  },
  purpleHeader: {
    backgroundColor: colors['color-ui2-brand-600'],
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  selectionToggleLabel: {
    color: colors['color-text-white'],
    width: 120,
  },
  bulkActionButton: {
    paddingRight: 8,
  },
  clearSelectionsButton: {
    paddingRight: 8,
  },
  scrollContainer: {
    maxHeight: 300,
  },
})

const selectedDocumentsKeyExtractor = (documentORM: DocumentORM, index: number): string =>
  `selected-${documentORM.model.id}-${index}`

type SelectedDocumentsFlatListProps = {
  selectedDocuments: DocumentORM[]
  renderItem: ListRenderItem<DocumentORM>
}

const SelectedDocumentsFlatList: React.FC<SelectedDocumentsFlatListProps> = ({
  selectedDocuments,
  renderItem,
}) => (
  <FlatList
    data={selectedDocuments}
    keyExtractor={selectedDocumentsKeyExtractor}
    renderItem={renderItem}
    scrollEnabled
  />
)

type SelectedCollapsibleCardProps = {
  selectedDocuments: DocumentORM[],
  onSelectDocuments: (documents: DocumentORM[]) => void,
  setSelectedDocuments: Dispatch<SetStateAction<SelectedDocuments>>,
  displayMode: DisplayModes,
  isSearch?: boolean,
}

const SelectedCollapsibleCard: React.FC<SelectedCollapsibleCardProps> = (props) => {
  const { selectedDocuments, onSelectDocuments, setSelectedDocuments, displayMode, isSearch } = props
  const featureFlags =
  useFeatureFlags('enableBulkAddToFolder', 'enableBulkShareEmail')
  const [isCollapsed, setIsCollapsed] = useState(true);
  const userTenant = useUserTenant()
  const { isOnline, deviceMode } = useAppSettings()
  const isDesktop = deviceMode === 'desktop';

  const fieldConfigs = getFieldConfigsForTenant(userTenant, Variant.MSL, isDesktop, true)
    .filter(field => {
      // Only keep Thumbnail, Name (Title), Badges (LABEL with isBadge), and Action Menu
      return (
        field.fieldName === 'thumbnail' ||
        field.fieldName === 'title' ||
        field.fieldType === 'ACTION_MENU'
      )
    })
  const DNADocumentActions = useDNADocumentActions()
  const cols = fieldConfigs
    .reduce(
      (acc, field) => {
        if (field.fieldType === 'ACTION_MENU' || selectedDocuments.length) {
          const widthStr = field.fieldName === 'title' ? '1fr' : `${field.width}px`
          return `${acc} ${widthStr}`
        }
        return acc
      },
      '',
    )
  // if none of the selected document are shareable or if user is not online, then disabled the share button
  const isShareButtonDisabled = !selectedDocuments.some(docVer => docVer.meta.permissions.MSLShare) || !isOnline

  // Check if all selected documents are bookmarked
  const allBookmarked = selectedDocuments.every(doc => doc.meta.bookmark?.isBookmarked)
  const favoriteButtonLabel = allBookmarked ? 'Unfavorite' : 'Favorite'
  const favoriteButtonIcon = allBookmarked ? 'star-fill' : 'star'

  const renderSelectedItem = ({ item : documentORM, index } : { item: DocumentORM, index: number } ) => {
    return (
      <>
        {index ? <DNADivider /> : null}
        <DNADocumentListRow
          isOnline={isOnline}
          isDesktop={isDesktop}
          documentORM={documentORM}
          variant={Variant.MSL}
          onPress={() => onSelectDocuments([documentORM])}
          onThumbnailPress={DNADocumentActions.present(documentORM)}
          fields={fieldConfigs}
          rowStyle={isDesktop ? undefined : S.MobileRow}
          cellStyle={S.DataCell}
          checked
          onCheck={() => onSelectDocuments([documentORM])}
          renderBadgesInline
        />
      </>
    )
  }

  const renderSearchSelectedItem = ({ item : documentORM, index } : { item: DocumentORM, index: number } ) => {
    return (
      <DNABox>
        {index ? <DNADivider /> : null}
        <DocumentSearchResultRow
          documentORM={documentORM}
          selectEnabled
          onCheck={() => onSelectDocuments([documentORM])}
          checked
        />
      </DNABox>
    )
  }

  if (!selectedDocuments.length) {
    return null
  }

  return (
    <DNABox fill appearance="col" style={S.accordionContainer}>
      {/* Purple Header */}
      <Pressable onPress={() => setIsCollapsed(!isCollapsed)}>
        <DNABox
          fill
          appearance="row"
          alignY="center"
          spacing="between"
          style={S.purpleHeader}
        >
          <DNABox appearance="row" alignY="center">
            <DNABox appearance="row" alignY="center" spacing="sm">
              <DNAText bold style={S.selectionToggleLabel}>
                {isCollapsed ? 'View selections' : 'Hide selections'}
              </DNAText>
              <DNAChip appearance="filled" status="danger">
                {`${selectedDocuments.length}`}
              </DNAChip>
            </DNABox>

            <DNADivider vertical style={{ marginLeft: 8 }} />

            {/* Center section: action buttons */}
            <Iffy is={selectedDocuments.length}>
              <DNABox appearance="row" spacing="sm" alignY="center">
                {featureFlags.enableBulkAddToFolder && <DNAButton
                  iconLeft="folder-simple-plus"
                  onPress={DNADocumentActions.bulkAddToFolder(selectedDocuments)}
                  style={S.bulkActionButton}
                >
                  Add to folder
                </DNAButton>}
                {featureFlags.enableBulkShareEmail && <DNAButton
                  iconLeft="envelope-simple"
                  onPress={DNADocumentActions.bulkShareEmail(selectedDocuments)}
                  disabled={isShareButtonDisabled}
                  style={S.bulkActionButton}
                >
                  Share
                </DNAButton>}
                <DNAButton
                  iconLeft={favoriteButtonIcon}
                  onPress={DNADocumentActions.bulkFavorite(selectedDocuments)}
                  style={S.bulkActionButton}
                >
                  {favoriteButtonLabel}
                </DNAButton>
              </DNABox>
            </Iffy>
          </DNABox>

          {/* Right section: Clear button - stays at the end */}
          <Iffy is={selectedDocuments.length}>
            <DNAButton
              appearance="filled"
              status="danger"
              onPress={() => setSelectedDocuments({})}
              style={S.clearSelectionsButton}
            >
              Clear selections
            </DNAButton>
          </Iffy>
        </DNABox>

        {/* Divider */}
        {!isCollapsed && <DNADivider />}
      </Pressable>

      {/* Content - shown when expanded */}
      <Iffy is={!isCollapsed && selectedDocuments.length > 0}>
        <DNABox
          appearance="col"
          style={[S.scrollContainer, { width: '100%' }]}
        >
          <Iffy is={displayMode === DisplayModes.list && !isSearch}>
            <GridList
              cols={cols}
              gap="12px"
              style={{ width: '100%' }}
            >
              <SelectedDocumentsFlatList
                selectedDocuments={selectedDocuments}
                renderItem={renderSelectedItem}
              />
            </GridList>
          </Iffy>
          <Iffy is={isSearch}>
            <SelectedDocumentsFlatList
              selectedDocuments={selectedDocuments}
              renderItem={renderSearchSelectedItem}
            />
          </Iffy>
          <Iffy is={displayMode === DisplayModes.grid && !isSearch}>
            <DNABox
              fill
              wrap="start"
              spacing="md"
              childStyle={S.cardSpacerStyle}
            >
              {selectedDocuments.map((documentORM, index) => (
                <DNADocumentDefaultCard
                  key={`selected-${documentORM.model.id}-${index}`}
                  isOnline={isOnline}
                  isDesktop={isDesktop}
                  ORM={documentORM}
                  onPress={DNADocumentActions.preview(documentORM)}
                  checked
                  onSelect={() => onSelectDocuments([documentORM])}
                />
              ))}
            </DNABox>
          </Iffy>
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

export default SelectedCollapsibleCard
